/**
 * /progress — Progress overview + full curriculum node detail.
 *
 * Merges the track-level Stats view (radar, sparkline, weak spots) with the
 * node-level Progress view (22 expandable node cards grouped by track).
 */

import { type ReactNode, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  buildAllItems,
  buildReviewPoints,
  MVP_NODES,
  MASTERY_PRESETS,
  RADAR_TRACKS,
  GUITAR_RUNGS,
  nodeMastery,
  nodeExerciseRoute,
  forecastDue,
  radarData,
  computeInstrumentRungs,
  type ActiveTrack,
  type CurriculumNode,
  type ReviewPoint,
  type TrackMastery,
} from "@music-app/learning-system";
import {
  useCardsStore,
  usePrefsStore,
  getReviewsForItem,
} from "@music-app/persistence";
import { SkillTreeNode, type ItemDetail } from "@music-app/ui-components";
import { hasLessonForNode } from "@music-app/lessons/runtime";
import {
  summarizeSessions,
  dailyBuckets,
  currentStreak,
} from "../lib/session-stats.js";
import { useRecentSessions } from "../lib/use-recent-sessions.js";
import { Sparkline } from "../components/Sparkline.js";
import { SparklineSkeleton } from "../components/SparklineSkeleton.js";
import { top3WeakSpots, type WeakSpot } from "../lib/coach-plan.js";
import { WeakSpotCard } from "../components/WeakSpotCard.js";
import { MasteryLegend } from "../components/MasteryLegend.js";
import { PageHeader } from "../components/PageHeader.js";
import { InstrumentProgressSection } from "../components/InstrumentProgressSection.js";

// ─── constants ────────────────────────────────────────────────────────────────

const SPARKLINE_DAYS = 14;
const FORECAST_DAYS = 30;
const ITEMS_PER_MINUTE = 6;

const TRACK_TO_EXERCISE: Readonly<Record<ActiveTrack, string>> = {
  pitch: "/exercise/scales",
  modes: "/exercise/modes",
  intervals: "/exercise/intervals",
  harmony: "/exercise/chords",
  dictation: "/exercise/dictation",
};

const TRACK_LABELS: Record<CurriculumNode["track"], string> = {
  pitch: "i · pitch & rhythm",
  modes: "ii · modes",
  intervals: "iii · intervals & scales",
  harmony: "iv · harmony & chords",
  dictation: "v · dictation",
  counterpoint: "vi · counterpoint",
  // Opt-in instrument track; gets its own Progress section in a later PR.
  guitar: "guitar",
};

const GUITAR_DRILL_ROUTES: Readonly<Record<string, string>> = {
  "fret-intervals":       "/guitar/fret-intervals",
  "fret-scale-degrees":   "/guitar/fret-scale-degrees",
  "fret-triads":          "/guitar/fret-triads",
  "fret-inversions":      "/guitar/fret-inversions",
  "fret-caged":           "/guitar/fret-caged",
  "fret-caged-anchoring": "/guitar/fret-caged-anchoring",
  "fret-scale-degrees-caged": "/guitar/fret-scale-degrees-caged",
  "fret-diatonic":        "/guitar/fret-diatonic",
  "fret-diatonic-minor":  "/guitar/fret-diatonic-minor",
  "fret-seventh":         "/guitar/fret-seventh",
};

// Oxblood hex used for SVG fills/strokes (matches design token `accent`).
const OXBLOOD = "#872932";
const INK = "#1A1813";

// ─── page ─────────────────────────────────────────────────────────────────────

export function ProgressPage(): ReactNode {
  // ── store data ───────────────────────────────────────────────────────────────
  const cards = useCardsStore((s) => s.cards);
  const masteryPreset = usePrefsStore((s) => s.masteryPreset);
  const sessionTargetMinutes = usePrefsStore((s) => s.sessionTargetMinutes);
  const enabledInstrumentTracks = usePrefsStore((s) => s.enabledInstrumentTracks);
  const thresholds = MASTERY_PRESETS[masteryPreset];

  // ── shared items ─────────────────────────────────────────────────────────────
  const items = useMemo(() => buildAllItems(), []);
  const guitarEnabled = enabledInstrumentTracks.includes("guitar");
  const guitarNodes = useMemo(() => MVP_NODES.filter((n) => n.track === "guitar"), []);
  const guitarRungs = useMemo(
    () =>
      guitarEnabled
        ? computeInstrumentRungs(GUITAR_RUNGS, items, cards, thresholds)
        : [],
    [guitarEnabled, items, cards, thresholds],
  );

  // ── session stats ────────────────────────────────────────────────────────────
  const { sessions, loaded } = useRecentSessions(60);
  const today = useMemo(() => new Date(), []);
  const summary = useMemo(() => summarizeSessions(sessions), [sessions]);
  const buckets = useMemo(
    () => dailyBuckets(sessions, today, SPARKLINE_DAYS),
    [sessions, today],
  );
  const streak = useMemo(() => currentStreak(sessions, today), [sessions, today]);
  const forecast = useMemo(
    () => forecastDue(cards, FORECAST_DAYS, today),
    [cards, today],
  );
  const recentSessionCount = useMemo(() => {
    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() - SPARKLINE_DAYS);
    return sessions.filter(
      (s) => s.endedAt !== null && new Date(s.startedAt) >= cutoff,
    ).length;
  }, [sessions, today]);

  // ── card aggregates ──────────────────────────────────────────────────────────
  const totalReps = useMemo(
    () => Object.values(cards).reduce((s, c) => s + c.reps, 0),
    [cards],
  );
  const totalLapses = useMemo(
    () => Object.values(cards).reduce((s, c) => s + c.lapses, 0),
    [cards],
  );

  // ── radar ────────────────────────────────────────────────────────────────────
  const radar = useMemo(
    () => radarData(items, cards, thresholds),
    [items, cards, thresholds],
  );
  const totalMastered = radar.reduce((s, t) => s + t.items.mastered, 0);
  const totalItems = radar.reduce((s, t) => s + t.items.total, 0);
  const noActivity = loaded && summary.sessionCount === 0 && totalReps === 0;

  // ── side rail ────────────────────────────────────────────────────────────────
  const strongest = useMemo(
    () => [...radar].sort((a, b) => b.mastery - a.mastery)[0] ?? null,
    [radar],
  );
  const weakest = useMemo(
    () => [...radar].sort((a, b) => a.mastery - b.mastery)[0] ?? null,
    [radar],
  );
  const readyToPush = useMemo(() => {
    const candidates = radar.filter((t) => t.mastery >= 0.1 && t.mastery <= 0.5);
    if (candidates.length === 0) return null;
    return (
      candidates.sort(
        (a, b) =>
          (1 - b.mastery) * b.items.total - (1 - a.mastery) * a.items.total,
      )[0] ?? null
    );
  }, [radar]);

  // ── weak spots ───────────────────────────────────────────────────────────────
  const weakSpots = useMemo(
    () => top3WeakSpots(items, cards, MVP_NODES),
    [items, cards],
  );

  const sessionCap = sessionTargetMinutes * ITEMS_PER_MINUTE;

  // ── node review histories (lazy-loaded on expand) ────────────────────────────
  const [nodeHistories, setNodeHistories] = useState<
    Map<string, Record<string, ReviewPoint[]>>
  >(new Map());

  const handleNodeExpand = useCallback(
    async (nodeId: string, expanded: boolean) => {
      if (!expanded || nodeHistories.has(nodeId)) return;
      const nodeItems = items.filter((i) => i.nodeId === nodeId);
      const pairs = await Promise.all(
        nodeItems.map(async (item) => {
          const records = await getReviewsForItem(item.id, 100);
          return [item.id, buildReviewPoints(records)] as const;
        }),
      );
      setNodeHistories((prev) => new Map(prev).set(nodeId, Object.fromEntries(pairs)));
    },
    [nodeHistories, items],
  );

  return (
    <div className="max-w-5xl mx-auto py-9 px-8">
      <PageHeader
        eyebrow="curriculum"
        title="Progress"
        subhead={
          guitarEnabled
            ? "Five tracks of the curriculum, scored from 0 to 100 by item mastery. The shape is what your ear is built of. Guitar rungs are below."
            : "Five tracks of the curriculum, scored from 0 to 100 by item mastery. The shape is what your ear is built of."
        }
      />

      {/* ── stat strip ── */}
      <section className="grid grid-cols-2 md:grid-cols-4 border border-ink/10 mb-8">
        <StatTile
          label="Streak"
          value={streak === 0 ? "—" : `${streak}`}
          sublabel={streak === 1 ? "day" : "days"}
        />
        <StatTile
          label="Accuracy"
          value={
            summary.totalReviews > 0
              ? `${Math.round(summary.accuracy * 100)}%`
              : "—"
          }
          bordered
        />
        <StatTile
          label="Sessions"
          value={`${recentSessionCount}`}
          sublabel="14 days"
          bordered
        />
        <StatTile
          label="Mastered"
          value={totalItems > 0 ? `${totalMastered} / ${totalItems}` : "—"}
          bordered
        />
      </section>

      {/* ── radar + side rail ── */}
      <section
        className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-10"
        aria-label="Skill mastery overview"
      >
        <div className="lg:col-span-3 flex flex-col items-center">
          {noActivity ? (
            <EmptyRadar />
          ) : (
            <SkillRadar
              data={radar}
              totalMastered={totalMastered}
              totalItems={totalItems}
            />
          )}
        </div>
        <div className="lg:col-span-2 flex flex-col gap-4">
          {noActivity ? (
            <div className="border border-ink/20 bg-paperWarm p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">
                no data yet
              </p>
              <p className="font-serif italic text-base text-ink mb-3">
                Complete a placement test to seed your radar.
              </p>
              <Link
                to="/placement"
                className="font-mono text-xs uppercase tracking-[0.18em] text-accent hover:underline"
              >
                Take placement →
              </Link>
            </div>
          ) : (
            <>
              {strongest && <SideRailCard tier="strongest" track={strongest} />}
              {weakest && weakest.track !== strongest?.track && (
                <SideRailCard tier="weakest" track={weakest} />
              )}
              {readyToPush && readyToPush.track !== weakest?.track && (
                <SideRailCard tier="push" track={readyToPush} />
              )}
            </>
          )}
        </div>
      </section>

      {/* ── weak spots ── */}
      {weakSpots.length > 0 && !noActivity && (
        <section className="mb-10" aria-label="Weak spots">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
            weak spots
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {weakSpots.map((spot: WeakSpot) => (
              <WeakSpotCard key={spot.node.id} spot={spot} period="lifetime" />
            ))}
          </div>
        </section>
      )}

      {/* ── sparkline + forecast ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <section className="border border-ink/10 p-5">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-serif italic text-lg text-ink">
              Last {SPARKLINE_DAYS} days
            </h2>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded">
              accuracy · activity
            </span>
          </div>
          {loaded ? (
            <Sparkline
              points={buckets.map((b) => ({
                date: b.date,
                value: b.accuracy,
                weight: b.reviews,
              }))}
              ariaLabel={`Daily accuracy for the last ${SPARKLINE_DAYS} days`}
            />
          ) : (
            <SparklineSkeleton />
          )}
          <div className="flex justify-between text-[10px] font-mono text-inkFaded mt-2 tabular-nums">
            <span>{buckets[0]?.date ?? ""}</span>
            <span>{buckets[buckets.length - 1]?.date ?? ""}</span>
          </div>
        </section>

        {totalReps > 0 && (
          <section
            className="border border-ink/10 p-5"
            aria-label="30-day review forecast"
          >
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-serif italic text-lg text-ink">
                30-day forecast
              </h2>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded">
                reviews / day
              </span>
            </div>
            <ForecastChart forecast={forecast} sessionCap={sessionCap} />
          </section>
        )}
      </div>

      {/* ── curriculum node grid ── */}
      <section aria-label="Curriculum" className="mb-10">
        <div className="flex items-center gap-4 mb-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded shrink-0">
            curriculum
          </p>
          <hr className="flex-1 border-ink/10" />
        </div>
        <MasteryLegend />
        {RADAR_TRACKS.map((track) => {
          const trackRadar = radar.find((t) => t.track === track) ?? null;
          const trackNodes = MVP_NODES.filter((n) => n.track === track);
          return (
            <section key={track} aria-labelledby={`track-${track}`} className="mb-10">
              <div className="flex items-center gap-4 mb-4 mt-8">
                <h2
                  id={`track-${track}`}
                  className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded shrink-0"
                >
                  {TRACK_LABELS[track]}
                </h2>
                <hr className="flex-1 border-ink/10" />
                {trackRadar && (
                  <span className="font-mono text-[10px] text-inkFaded tabular-nums shrink-0">
                    {trackRadar.items.mastered}/{trackRadar.items.total} mastered
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {trackNodes.map((node) => {
                  const m = nodeMastery(node, items, cards, thresholds);
                  const prereqNames = node.prerequisites
                    .map((id) => MVP_NODES.find((n) => n.id === id)?.title)
                    .filter((t): t is string => t !== undefined);
                  const history = nodeHistories.get(node.id);
                  const nodeItems: ItemDetail[] = items
                    .filter((i) => i.nodeId === node.id)
                    .map((i) => {
                      const card = cards[i.id];
                      return {
                        id: i.id,
                        label: i.label,
                        stability: card?.stability ?? 0,
                        due: card?.due ?? null,
                        lapses: card?.lapses ?? 0,
                        state: card?.state ?? "new",
                        reviewHistory: history?.[i.id],
                      };
                    });
                  return (
                    <SkillTreeNode
                      key={node.id}
                      id={node.id}
                      title={node.title}
                      track={TRACK_LABELS[node.track]}
                      tier={m.tier}
                      mastered={m.masteredCount}
                      practicing={m.practicingCount}
                      introduced={m.introducedCount}
                      total={m.totalCount}
                      prereqNames={prereqNames}
                      lessonHref={
                        hasLessonForNode(node.id) ? `/learn/${node.id}` : undefined
                      }
                      drillHref={nodeExerciseRoute(node.id)?.path}
                      items={nodeItems}
                      masteredThreshold={thresholds.mastered}
                      onExpandChange={(expanded) =>
                        void handleNodeExpand(node.id, expanded)
                      }
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </section>

      {/* ── instrument tracks ── */}
      {guitarEnabled && (
        <InstrumentProgressSection
          instrumentLabel="Guitar"
          rungs={guitarRungs}
          nodes={guitarNodes}
          items={items}
          cards={cards}
          thresholds={thresholds}
          drillRoutes={GUITAR_DRILL_ROUTES}
          nodeHistories={nodeHistories}
          onNodeExpand={handleNodeExpand}
        />
      )}

      {/* ── lifetime footer ── */}
      <p className="text-xs text-inkFaded font-mono">
        Lifetime: {totalReps} reviews · {totalLapses} lapses ·{" "}
        {totalReps > 0
          ? `${Math.round((totalLapses / totalReps) * 100)}%`
          : "0%"}{" "}
        lapse rate
      </p>
    </div>
  );
}

// ─── SkillRadar ───────────────────────────────────────────────────────────────

interface RadarProps {
  data: TrackMastery[];
  totalMastered: number;
  totalItems: number;
}

function SkillRadar({ data, totalMastered, totalItems }: RadarProps): ReactNode {
  const CX = 260;
  const CY = 240;
  const R = 170;
  const LABEL_OFFSET = 30;
  const N = data.length;

  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / N;
  const pt = (i: number, f: number) => ({
    x: CX + R * f * Math.cos(angle(i)),
    y: CY + R * f * Math.sin(angle(i)),
  });
  const poly = (pts: { x: number; y: number }[]) =>
    pts.map((p, j) => `${j === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ") + " Z";

  const rings = [0.25, 0.5, 0.75, 1.0].map((f) =>
    poly(data.map((_, i) => pt(i, f))),
  );
  const masteryPoly = poly(data.map((t, i) => pt(i, t.mastery)));

  const labelAnchor = (i: number): { anchor: "start" | "middle" | "end"; dy: number } => {
    const a = angle(i);
    const sinA = Math.sin(a);
    const cosA = Math.cos(a);
    const anchor =
      Math.abs(cosA) < 0.15 ? "middle" : cosA > 0 ? "start" : "end";
    const dy = sinA < -0.15 ? -6 : sinA > 0.15 ? 16 : 6;
    return { anchor, dy };
  };

  const ariaLabel = `Mastery radar. ${data
    .map(
      (t) =>
        `${t.label} ${Math.round(t.mastery * 100)}%${t.mastery <= 0.25 ? " (weakest)" : ""}`,
    )
    .join(", ")}.`;

  return (
    <svg
      viewBox="0 0 520 500"
      className="w-full max-w-md"
      role="img"
      aria-label={ariaLabel}
    >
      {rings.map((d, ri) => (
        <path
          key={ri}
          d={d}
          fill="none"
          stroke={ri === rings.length - 1 ? INK : `${INK}55`}
          strokeWidth={ri === rings.length - 1 ? 1 : 0.6}
          strokeDasharray={ri === rings.length - 1 ? undefined : "3 3"}
        />
      ))}
      {data.map((_, i) => {
        const outer = pt(i, 1);
        return (
          <line
            key={i}
            x1={CX}
            y1={CY}
            x2={outer.x}
            y2={outer.y}
            stroke={`${INK}33`}
            strokeWidth={0.8}
          />
        );
      })}
      <path
        d={masteryPoly}
        fill={`${OXBLOOD}2E`}
        stroke={OXBLOOD}
        strokeWidth={1.8}
        strokeLinejoin="round"
      />
      {data.map((t, i) => {
        const p = pt(i, t.mastery);
        const isWeak = t.mastery <= 0.25;
        return (
          <g key={i}>
            {isWeak && (
              <circle
                cx={p.x}
                cy={p.y}
                r={11}
                fill="none"
                stroke={OXBLOOD}
                strokeWidth={1.2}
                strokeDasharray="3 2"
              >
                <title>Weak spoke — needs attention</title>
              </circle>
            )}
            <circle cx={p.x} cy={p.y} r={4} fill={OXBLOOD} />
          </g>
        );
      })}
      {data.map((t, i) => {
        const tip = pt(i, 1);
        const a = angle(i);
        const lx = tip.x + LABEL_OFFSET * Math.cos(a);
        const ly = tip.y + LABEL_OFFSET * Math.sin(a);
        const { anchor, dy } = labelAnchor(i);
        const isWeak = t.mastery <= 0.25;
        const pct = Math.round(t.mastery * 100);
        return (
          <g key={i}>
            <text
              x={lx}
              y={ly + dy}
              textAnchor={anchor}
              fontFamily="Georgia, 'Times New Roman', serif"
              fontStyle={isWeak ? "italic" : "normal"}
              fontSize={13}
              fill={isWeak ? OXBLOOD : INK}
            >
              {t.label}
            </text>
            <text
              x={lx}
              y={ly + dy + 15}
              textAnchor={anchor}
              fontFamily="'Courier New', Courier, monospace"
              fontSize={10}
              fill={`${INK}88`}
            >
              {pct}% · {t.items.mastered}/{t.items.total}
            </text>
          </g>
        );
      })}
      <text
        x={CX}
        y={CY - 10}
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize={22}
        fontWeight="500"
        fill={INK}
      >
        {totalMastered}/{totalItems}
      </text>
      <text
        x={CX}
        y={CY + 8}
        textAnchor="middle"
        fontFamily="'Courier New', Courier, monospace"
        fontSize={9}
        fill={`${INK}88`}
        letterSpacing={1}
      >
        ITEMS MASTERED
      </text>
      <g transform="translate(50, 470)">
        <rect x={0} y={-6} width={10} height={10} fill={`${OXBLOOD}2E`} stroke={OXBLOOD} strokeWidth={1} />
        <text x={14} y={4} fontFamily="'Courier New', Courier, monospace" fontSize={9} fill={`${INK}99`}>YOUR EAR</text>
        <circle cx={110} cy={0} r={5} fill="none" stroke={OXBLOOD} strokeWidth={1.2} strokeDasharray="2 1.5" />
        <text x={120} y={4} fontFamily="'Courier New', Courier, monospace" fontSize={9} fill={`${INK}99`}>WEAK SPOKE</text>
        <circle cx={230} cy={0} r={5} fill="none" stroke={INK} strokeWidth={1} />
        <text x={240} y={4} fontFamily="'Courier New', Courier, monospace" fontSize={9} fill={`${INK}99`}>FULL MASTERY</text>
      </g>
    </svg>
  );
}

function EmptyRadar(): ReactNode {
  const CX = 260;
  const CY = 240;
  const R = 170;
  const N = 5;
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / N;
  const outerPts = Array.from({ length: N }, (_, i) => ({
    x: CX + R * Math.cos(angle(i)),
    y: CY + R * Math.sin(angle(i)),
  }));
  const d =
    outerPts.map((p, j) => `${j === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ") + " Z";
  return (
    <svg
      viewBox="0 0 520 500"
      className="w-full max-w-md"
      role="img"
      aria-label="Mastery radar — no data yet"
    >
      <path d={d} fill="none" stroke={`${INK}33`} strokeWidth={1} />
      <text
        x={CX}
        y={CY}
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontStyle="italic"
        fontSize={16}
        fill={`${INK}66`}
      >
        no data yet
      </text>
    </svg>
  );
}

// ─── side rail ────────────────────────────────────────────────────────────────

function SideRailCard({
  tier,
  track,
}: {
  tier: "strongest" | "weakest" | "push";
  track: TrackMastery;
}): ReactNode {
  const cfg = {
    strongest: {
      label: "STRONGEST SPOKE",
      labelColor: "text-success",
      desc: `${track.items.mastered} of ${track.items.total} items mastered`,
      link: null as string | null,
      drillLink: null as string | null,
    },
    weakest: {
      label: "WEAKEST SPOKE",
      labelColor: "text-accent",
      desc:
        track.mastery === 0
          ? `0 of ${track.items.total} items · not yet started`
          : `${track.items.mastered} of ${track.items.total} items`,
      link: `/learn?track=${track.track}` as string | null,
      drillLink: `${TRACK_TO_EXERCISE[track.track]}?mode=review` as string | null,
    },
    push: {
      label: "READY TO PUSH",
      labelColor: "text-accent",
      desc: `${track.items.mastered} of ${track.items.total} items · room to grow`,
      link: `/learn?track=${track.track}` as string | null,
      drillLink: `${TRACK_TO_EXERCISE[track.track]}?mode=review` as string | null,
    },
  }[tier];

  return (
    <div className="border border-ink/10 p-4 bg-paper">
      <p className={`font-mono text-[10px] uppercase tracking-[0.18em] mb-1 ${cfg.labelColor}`}>
        {cfg.label}
      </p>
      <p className="font-serif text-2xl font-medium text-ink mb-0.5">{track.label}</p>
      <p className="font-mono text-xs text-inkFaded mb-2">{cfg.desc}</p>
      {(cfg.link || cfg.drillLink) && (
        <div className="flex gap-4">
          {cfg.link && (
            <Link
              to={cfg.link}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:underline"
            >
              Study this →
            </Link>
          )}
          {cfg.drillLink && (
            <Link
              to={cfg.drillLink}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:underline"
            >
              Drill this →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ─── stat tile ────────────────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  sublabel,
  bordered,
}: {
  label: string;
  value: string;
  sublabel?: string;
  bordered?: boolean;
}): ReactNode {
  const isEmpty = value === "—";
  return (
    <div className={`p-5 ${bordered ? "border-l border-ink/10" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1 font-medium">
        {label}
      </div>
      <div
        className="font-serif text-2xl font-medium text-ink tabular-nums"
        aria-label={isEmpty ? `${label}: no data yet` : undefined}
      >
        <span aria-hidden={isEmpty || undefined}>{value}</span>
        {sublabel && (
          <span className="text-sm text-inkSoft ml-1 font-serif italic">
            {sublabel}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── forecast chart ───────────────────────────────────────────────────────────

function ForecastChart({
  forecast,
  sessionCap,
}: {
  forecast: number[];
  sessionCap: number;
}): ReactNode {
  const maxCount = Math.max(...forecast, sessionCap, 1);

  if (forecast.every((n) => n === 0)) {
    return (
      <p className="font-mono text-xs text-inkFaded italic">
        No scheduled reviews in the next 30 days.
      </p>
    );
  }

  const barW = 6;
  const gap = 2;
  const padX = 4;
  const padY = 4;
  const chartH = 60;
  const innerH = chartH - padY * 2;
  const totalW = padX * 2 + forecast.length * (barW + gap) - gap;
  const capLineY = padY + innerH * (1 - sessionCap / maxCount);

  return (
    <div>
      <svg
        viewBox={`0 0 ${totalW} ${chartH}`}
        preserveAspectRatio="none"
        className="w-full h-16"
        role="img"
        aria-label="30-day review forecast bar chart"
      >
        {sessionCap > 0 && sessionCap <= maxCount && (
          <line
            x1={padX}
            y1={capLineY}
            x2={totalW - padX}
            y2={capLineY}
            strokeWidth={0.5}
            strokeDasharray="2 2"
            stroke="rgba(26,24,19,0.3)"
          />
        )}
        {forecast.map((count, i) => {
          const x = padX + i * (barW + gap);
          const barH = count === 0 ? 1 : Math.max(2, (count / maxCount) * innerH);
          const y = padY + innerH - barH;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barW}
              height={barH}
              fill={
                count > sessionCap && sessionCap > 0
                  ? "rgba(163,45,45,0.65)"
                  : "rgba(26,24,19,0.35)"
              }
              aria-label={`Day ${i}: ${count} review${count === 1 ? "" : "s"}`}
            />
          );
        })}
      </svg>
      <div className="flex justify-between font-mono text-[10px] text-inkFaded mt-1">
        <span>today</span>
        <span>+15 days</span>
        <span>+30 days</span>
      </div>
    </div>
  );
}
